import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { getAccessToken, refreshAccessToken } from '@/contexts/AuthContext'

// Live updates pushed by the backend over `ws/live/` when an MCP agent (or the
// in-app builder) writes the user's data. Each frame either invalidates the
// matching React Query caches (so the open page refreshes without a reload) or
// drives the "AI is editing…" presence banner.

export type PresenceState = 'working' | 'idle'

export interface Presence {
  state: PresenceState
  detail?: string | null
}

export interface LiveUpdatesValue {
  /** True while an MCP agent has been active recently (activity-decay window). */
  connected: boolean
  /** Non-null while an agent is actively editing (lingers briefly after idle). */
  presence: Presence | null
  /** Keys `${resourceType}:${id}` changed in the last few seconds (for highlight). */
  recentlyChanged: ReadonlySet<string>
}

interface UpdateFrame {
  type: 'update'
  resource_type: string
  action: string
  id?: string | number | null
}

interface PresenceFrame {
  type: 'presence'
  state: PresenceState | 'connected'
  detail?: string | null
}

type Frame = UpdateFrame | PresenceFrame

// resource_type -> React Query key prefixes to invalidate. Prefix-matching means
// e.g. ['views'] also invalidates ['views', id], and ['activity-events']
// invalidates every windowed ['activity-events', planId, start, end] query.
// A key listed here must be one a SCREEN actually reads. Prefix-matching does
// not rescue a near miss: ['activity-plans'] is not a prefix of
// ['periodizations'], so a frame naming the wrong key invalidates something the
// write never touched and the screen showing the change stays stale — with the
// frame arriving, the handler running, and nothing to see. Two rows below were
// added for exactly that reason (beoflow/beoflow#2010):
//
//   periodizations       — phase bars in useCalendar.ts and PeriodizationPage
//                          read ['periodizations'] (services/api/periodization.ts),
//                          NOT ['activity-plans'], which is plan metadata.
//   calendar-week-widget — the pinned week widget on CalendarPage reads its own
//                          key (services/api/widgets.ts), reachable from neither
//                          'view' nor 'widget'.
const RESOURCE_KEYS: Record<string, readonly unknown[][]> = {
  program: [['activity-plans'], ['periodizations']],
  workout: [['activity-events'], ['activity-feed'], ['activity-plans']],
  template: [['workout-templates'], ['structured-workouts']],
  exercise: [['custom-exercises'], ['exercises']],
  view: [['views'], ['calendar-week-widget']],
  widget: [['widgets'], ['widget-library'], ['views'], ['calendar-week-widget']],
  // An MCP update_doc refreshes an open DocPage, its history and the docs list.
  doc: [['docs']],
}

// How long a changed item stays "recently changed" (drives the highlight pulse).
const HIGHLIGHT_MS = 2500
// How long the presence banner lingers after the last 'idle' so a burst of
// sequential tool calls reads as one continuous "AI is editing…" session.
const PRESENCE_LINGER_MS = 2000
// How long the "AI agent connected" pill stays lit after the last MCP activity
// (any tool call). Stateless MCP has no real connection, so "connected" means
// "active within this window".
const AGENT_DECAY_MS = 90_000
// Reconnect backoff bounds.
const RECONNECT_MIN_MS = 1000
const RECONNECT_MAX_MS = 15000

function wsUrl(token: string): string {
  const base = import.meta.env.VITE_WS_URL as string | undefined
  if (base) return `${base.replace(/\/$/, '')}/ws/live/?token=${encodeURIComponent(token)}`
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${window.location.host}/ws/live/?token=${encodeURIComponent(token)}`
}

export function useLiveUpdatesConnection(): LiveUpdatesValue {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()

  const [connected, setConnected] = useState(false)
  const [presence, setPresence] = useState<Presence | null>(null)
  const [recentlyChanged, setRecentlyChanged] = useState<ReadonlySet<string>>(() => new Set())

  // Timers and highlight bookkeeping kept in refs so they survive re-renders.
  const presenceClearRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const connectedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const highlightTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    if (!isAuthenticated) return

    // Capture the ref's Map once so the cleanup closure doesn't read a ref that
    // may have changed (react-hooks/exhaustive-deps); it's a stable instance.
    const timers = highlightTimers.current

    let socket: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0
    let closed = false
    let refreshing = false

    const markChanged = (resourceType: string, id?: string | number | null) => {
      if (id === undefined || id === null) return
      const key = `${resourceType}:${id}`
      setRecentlyChanged((prev) => {
        const next = new Set(prev)
        next.add(key)
        return next
      })
      const existing = timers.get(key)
      if (existing) clearTimeout(existing)
      timers.set(key, setTimeout(() => {
        timers.delete(key)
        setRecentlyChanged((prev) => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
      }, HIGHLIGHT_MS))
    }

    // Any frame from the server means the agent is active — relight the pill and
    // restart its decay countdown.
    const refreshConnected = () => {
      setConnected(true)
      if (connectedTimerRef.current) clearTimeout(connectedTimerRef.current)
      connectedTimerRef.current = setTimeout(() => {
        setConnected(false)
        setPresence(null)
      }, AGENT_DECAY_MS)
    }

    const handleUpdate = (frame: UpdateFrame) => {
      refreshConnected()
      const keys = RESOURCE_KEYS[frame.resource_type]
      if (keys) {
        for (const queryKey of keys) queryClient.invalidateQueries({ queryKey })
      }
      markChanged(frame.resource_type, frame.id)
    }

    const handlePresence = (frame: PresenceFrame) => {
      refreshConnected()
      if (frame.state === 'working') {
        if (presenceClearRef.current) {
          clearTimeout(presenceClearRef.current)
          presenceClearRef.current = null
        }
        setPresence({ state: 'working', detail: frame.detail })
      } else if (frame.state === 'idle') {
        // Linger so rapid back-to-back tool calls don't flicker the banner.
        if (presenceClearRef.current) clearTimeout(presenceClearRef.current)
        presenceClearRef.current = setTimeout(() => setPresence(null), PRESENCE_LINGER_MS)
      }
      // 'connected' frames only refresh the decay timer (no editing detail).
    }

    const scheduleReconnect = () => {
      if (closed) return
      const delay = Math.min(RECONNECT_MIN_MS * 2 ** attempt, RECONNECT_MAX_MS)
      attempt += 1
      reconnectTimer = setTimeout(connect, delay)
    }

    const connect = async () => {
      if (closed) return
      const token = getAccessToken()
      if (!token) {
        scheduleReconnect()
        return
      }
      socket = new WebSocket(wsUrl(token))

      socket.onopen = () => { attempt = 0 }

      socket.onmessage = (event) => {
        let frame: Frame
        try {
          frame = JSON.parse(event.data)
        } catch {
          return
        }
        if (frame.type === 'update') handleUpdate(frame)
        else if (frame.type === 'presence') handlePresence(frame)
      }

      socket.onclose = async (event) => {
        socket = null
        if (closed) return
        // 4401 = server rejected auth (expired/invalid token). Refresh once, then
        // reconnect with a fresh token instead of looping on a dead credential.
        if (event.code === 4401 && !refreshing) {
          refreshing = true
          try {
            await refreshAccessToken()
          } catch {
            // refresh failed — AuthContext's 401 path will log the user out
          } finally {
            refreshing = false
          }
        }
        scheduleReconnect()
      }

      socket.onerror = () => { socket?.close() }
    }

    connect()

    return () => {
      closed = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (presenceClearRef.current) clearTimeout(presenceClearRef.current)
      if (connectedTimerRef.current) clearTimeout(connectedTimerRef.current)
      setConnected(false)
      timers.forEach(clearTimeout)
      timers.clear()
      if (socket) {
        socket.onclose = null // don't reconnect on intentional teardown
        socket.close()
      }
    }
  }, [isAuthenticated, queryClient])

  return { connected, presence, recentlyChanged }
}
